import { Injectable, Injector, Inject, NgModule, ErrorHandler } from '@angular/core';
import { HttpErrorResponse, HTTP_INTERCEPTORS } from '@angular/common/http';
import { LoggerService, LoggerModule } from '@wdpr/ra-angular-logger';
import { LocationStrategy } from '@angular/common';
import { Subject, throwError } from 'rxjs';
import 'rxjs/add/observable/throw';
import { catchError } from 'rxjs/operators';
import isUndefined from 'lodash-es/isUndefined';

class ErrorService {
    constructor(_location, _logger) {
        this._location = _location;
        this._logger = _logger;
        this.UNCAUGHT_EXCEPTION = 'Exception caught in error service when trying to log error via logger service';
        this.ERROR_LOGGED_BY = 'Error logged by WDPR RA Angular Error handler service';
        this.UNKNOWN_CAUSE = 'unknown cause';
        this.errorServiceMessages$ = new Subject();
    }
    /**
     * Log an error
     * @param exception
     * @param cause
     */
    log(exception, cause = this.UNKNOWN_CAUSE) {
        try {
            const errorJson = {
                'page': this._location.path(),
                'timestamp': new Date().getTime(),
                'message': exception.message,
                'name': exception.name,
                'stack': exception.stack,
                'cause': cause
            };
            if (exception instanceof HttpErrorResponse) {
                errorJson.error = exception.error;
            }
            this._logger.error(this.ERROR_LOGGED_BY, JSON.stringify(errorJson));
            // Emit Event
            this.errorServiceMessages$.next([{ exception }, { cause }, { errorJson }]);
        }
        catch (logError) {
            // Log the error to the console as a last resort
            try {
                console.error(this.UNCAUGHT_EXCEPTION, logError);
            }
            catch (error) {
                // Don't cause a infinite loop by trying to catch the error here.
                // Just eat the error
            }
        }
    }
}
ErrorService.decorators = [
    { type: Injectable }
];
ErrorService.ctorParameters = () => [
    { type: LocationStrategy },
    { type: LoggerService }
];

class HttpInterceptorService {
    constructor(injector) {
        this.injector = injector;
        this.NO_RESPONSE = 'No response received.';
        this.NO_DATA = 'No data received.';
        this.HTTP_INTERCEPTOR_ERROR = 'in HttpInterceptorService::intercept';
    }
    /**
     * Intercept the request
     * @param request
     * @param next
     */
    intercept(request, next) {
        this._errorService = this.injector.get(ErrorService);
        this._loggerService = this.injector.get(LoggerService);
        // Return an observable
        // Listen to every event on the observable and then pass it along
        return next.handle(request)
            .pipe(catchError((response) => {
            if (response.url && response.url.indexOf(this._loggerService.url) < 0) {
                this._errorService.log(response, this.HTTP_INTERCEPTOR_ERROR);
            }
            else {
                console.error('Skipping the log to the failed ' + response.url + ' endpoint, to avoid infinite loop.');
            }
            // HttpErrorResponse handled here
            return throwError(response);
        }));
    }
}
HttpInterceptorService.decorators = [
    { type: Injectable }
];
HttpInterceptorService.ctorParameters = () => [
    { type: Injector, decorators: [{ type: Inject, args: [Injector,] }] }
];

// get a reference to the window object
function getWindow() {
    return window;
}
class WindowRef {
    getNativeWindow() {
        return getWindow();
    }
    /**
     * Check to see if the error handling should be enabled
     */
    isErrorHandlerEnabled() {
        let result = false;
        const windowObj = getWindow();
        // If define exists on the window object we're in test mode.
        if (windowObj && isUndefined(windowObj.define)) {
            result = true;
        }
        return result;
    }
}
WindowRef.decorators = [
    { type: Injectable }
];

class GlobalExceptionHandlingService {
    constructor(_errorService, _windowRef) {
        this._errorService = _errorService;
        this._windowRef = _windowRef;
        this.EXCEPTION_ERROR = 'Exception service throws exception when trying to log error using error service';
    }
    handleError(exception) {
        // Stop running if we're in a test env
        if (!this._windowRef.isErrorHandlerEnabled) {
            return;
        }
        try {
            this._errorService.log(exception);
        }
        catch (error) {
            // Log the error to the console as a last resort
            try {
                console.error(this.EXCEPTION_ERROR, error);
            }
            catch (logerror) {
                // Don't cause a infinite loop by trying to catch the error here.
                // Just eat the error
            }
        }
    }
}
GlobalExceptionHandlingService.decorators = [
    { type: Injectable }
];
GlobalExceptionHandlingService.ctorParameters = () => [
    { type: ErrorService, decorators: [{ type: Inject, args: [ErrorService,] }] },
    { type: WindowRef, decorators: [{ type: Inject, args: [WindowRef,] }] }
];

class ErrorHandlerModule {
}
ErrorHandlerModule.decorators = [
    { type: NgModule, args: [{
                imports: [LoggerModule],
                providers: [
                    ErrorService,
                    WindowRef,
                    {
                        provide: ErrorHandler,
                        useClass: GlobalExceptionHandlingService
                    },
                    {
                        provide: HTTP_INTERCEPTORS,
                        useClass: HttpInterceptorService,
                        multi: true
                    }
                ],
                declarations: []
            },] }
];

class CustomError {
}

/*
 * Public API Surface of ra-angular-error-handler
 */

/**
 * Generated bundle index. Do not edit.
 */

export { CustomError, ErrorHandlerModule, ErrorService, WindowRef as ɵa, GlobalExceptionHandlingService as ɵb, HttpInterceptorService as ɵc };
//# sourceMappingURL=wdpr-ra-angular-error-handler.js.map
